import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons'
import { Icon, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { ADVISER_COMPLIANCE_DISCLAIMER } from '../lib/constants'
import { ChatThinking } from './ChatThinking'

export interface ChatInputHandle {
  focus: () => void
  hasDraft: () => boolean
}

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  recipientHint?: string
  initialFocus?: boolean
  showDisclaimer?: boolean
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  function ChatInput(
    {
      onSend,
      disabled,
      placeholder = 'Ask a question or describe what you need...',
      recipientHint,
      initialFocus,
      showDisclaimer = true,
    },
    ref
  ) {
    const [value, setValue] = useState('')
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(
      ref,
      () => ({
        focus: () => textareaRef.current?.focus(),
        hasDraft: () => Boolean(textareaRef.current?.value.trim()),
      }),
      []
    )

    useEffect(() => {
      if (initialFocus) textareaRef.current?.focus()
    }, [initialFocus])

    // Auto-resize textarea — reset height first so scrollHeight reflects content
    useEffect(() => {
      const el = textareaRef.current
      if (!el) return
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`
    }, [value])

    // Restore focus when the textarea is re-enabled (disabled: true → false).
    // setTimeout(0) ensures this fires after all sibling effects (animation
    // cleanup, scroll-to-bottom, etc.) have run so nothing can steal focus back.
    const prevDisabledRef = useRef<boolean | undefined>(disabled)
    useEffect(() => {
      const wasDisabled = Boolean(prevDisabledRef.current)
      prevDisabledRef.current = disabled

      if (!wasDisabled || disabled) return undefined

      const id = setTimeout(() => {
        textareaRef.current?.focus()
      }, 0)
      return () => clearTimeout(id)
    }, [disabled])

    const handleSend = () => {
      const trimmed = value.trim()
      if (!trimmed || disabled) return
      onSend(trimmed)
      setValue('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }

    const isEmpty = !value.trim()
    const isDisabled = disabled || isEmpty

    return (
      <div className="flex flex-col gap-2">
        <div
          className={mtc(
            'flex items-end gap-2 bg-neutral-0',
            'border border-neutral-200 rounded-[8px]',
            'shadow-md',
            'focus-within:border-neutral-300 transition-colors'
          )}
        >
          {/* Textarea — grows with content */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={mtc(
              'flex-1 min-w-0 pl-4 py-[13px] min-h-[46px]',
              'adviser-chat-input bg-transparent body-sm text-neutral-800',
              'placeholder:truncate placeholder:text-sm placeholder:text-neutral-400',
              'outline-none border-none resize-none block',
              disabled && 'cursor-not-allowed'
            )}
          />

          <div className="shrink-0 pr-[7px] pb-[7px]">
            <button
              type="button"
              onClick={handleSend}
              disabled={isDisabled}
              aria-label="Send message"
              className={mtc(
                'adviser-send-btn',
                'flex items-center justify-center size-8 shrink-0 rounded-[8px]',
                'border border-neutral-900',
                'text-neutral-0 transition-[background-image,opacity]',
                isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                isEmpty && !disabled && 'hidden'
              )}
            >
              {disabled ? (
                <ChatThinking />
              ) : (
                <span className="flex items-center justify-center">
                  <Icon
                    icon={faPaperPlaneTop}
                    color="text-neutral-0"
                    size="xs"
                  />
                </span>
              )}
            </button>
          </div>
        </div>

        {recipientHint && (
          <Text
            variant={TypographyStyles.BODY_XS}
            color={TextColors.DISABLED}
            className="text-center"
          >
            {recipientHint}
          </Text>
        )}
        {showDisclaimer && <ComplianceDisclaimer />}
      </div>
    )
  }
)

export const ComplianceDisclaimer = () => (
  <Text
    variant={TypographyStyles.BODY_XS}
    color={TextColors.DISABLED}
    className="text-center"
  >
    {ADVISER_COMPLIANCE_DISCLAIMER}
  </Text>
)
